import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useRole } from "@/lib/roleContext";
import { CalendarDays, Users, Ticket, TrendingUp, MapPin, Globe2, AlertTriangle, ArrowUpRight, Heart, Sparkles, CheckCircle2, Circle, X } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";

const FOLLOWER_ROLES = new Set(["artist_owner", "artist_team", "admin"]);

export default function Overview() {
    const { activeArtistId, activeArtistName, role } = useRole();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [obStatus, setObStatus] = useState(null);
    const [dismissed, setDismissed] = useState(() => {
        try { return !!localStorage.getItem("pm_hide_ob_checklist"); } catch { return false; }
    });

    useEffect(() => {
        if (!activeArtistId) return;
        setLoading(true);
        endpoints
            .dashboard(activeArtistId)
            .then((d) => {
                setData(d);
                setLoading(false);
            })
            .catch(() => setLoading(false));
        endpoints.onboardingStatus(activeArtistId).then(setObStatus).catch(() => setObStatus(null));
        endpoints.getArtistVerification(activeArtistId).then(setVerification).catch(() => setVerification(null));
    }, [activeArtistId]);

    const [verification, setVerification] = useState(null);
    const [submittingVerification, setSubmittingVerification] = useState(false);
    const submitVerification = async () => {
        if (submittingVerification) return;
        setSubmittingVerification(true);
        try {
            const r = await endpoints.submitArtistVerification(activeArtistId);
            setVerification({ status: r.status });
            toast.success("Submitted for verification — Project Mint will review shortly");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object" && Array.isArray(detail.missing)) {
                toast.error(`Complete these first: ${detail.missing.join(", ")}`);
            } else {
                toast.error("Could not submit for verification");
            }
        } finally { setSubmittingVerification(false); }
    };

    const dismiss = () => {
        try { localStorage.setItem("pm_hide_ob_checklist", "1"); } catch {}
        setDismissed(true);
    };

    if (loading) return <div className="p-6 md:p-10 font-mono text-sm text-[#666]" data-testid="dashboard-loading">Loading dashboard…</div>;
    if (!data) return <div className="p-6 md:p-10 font-mono text-sm text-[#666]">Select an artist workspace.</div>;

    const s = data.summary;
    const fm = data.follower_metrics;
    const canSeeFollowers = FOLLOWER_ROLES.has(role);

    return (
        <div className="pm-fade" data-testid="dashboard-overview">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Overview</div>
                <div className="flex items-center justify-between gap-4">
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">
                        {activeArtistName}
                    </h1>
                    <Link to="/workspace/events/new" className="pm-btn pm-btn-primary" data-testid="dashboard-create-event">
                        + Create Event
                    </Link>
                </div>
            </header>

            {verification && verification.status !== "verified" && (
                <div
                    className={`border-b px-4 md:px-8 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 ${
                        verification.status === "pending"
                            ? "border-[#ffea00]/40 bg-[#ffea00]/5"
                            : verification.status === "needs_information"
                                ? "border-[#ff9500]/40 bg-[#ff9500]/5"
                                : verification.status === "rejected"
                                    ? "border-[#ff3333]/40 bg-[#ff3333]/5"
                                    : "border-[#2a2a2a] bg-[#141414]"
                    }`}
                    data-testid={`verification-banner-${verification.status}`}
                >
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">
                            {verification.status === "pending" && "Verification pending"}
                            {verification.status === "needs_information" && "More information requested"}
                            {verification.status === "rejected" && "Verification rejected"}
                            {verification.status === "unverified" && "Verify your artist"}
                        </div>
                        <div className="mt-1 text-sm text-[#f4f4f0] max-w-2xl">
                            {verification.status === "unverified" &&
                                "You can build privately now. Submit for manual verification to unlock public publishing."}
                            {verification.status === "pending" &&
                                "Project Mint is reviewing your submission. You can keep building your workspace while we review."}
                            {verification.status === "needs_information" &&
                                (verification.admin_message || "Project Mint needs more information before verifying this profile. Update your workspace and resubmit.")}
                            {verification.status === "rejected" &&
                                (verification.decision_reason || "Verification was rejected. Your workspace remains — contact support if you believe this was in error.")}
                        </div>
                    </div>
                    {(verification.status === "unverified" || verification.status === "needs_information") && (
                        <button
                            onClick={submitVerification}
                            disabled={submittingVerification}
                            className="pm-btn pm-btn-primary shrink-0"
                            data-testid="verification-submit-btn"
                        >
                            {submittingVerification ? "Submitting…" : "Submit for verification"}
                        </button>
                    )}
                </div>
            )}

            {obStatus && obStatus.percent < 100 && !dismissed && (
                <OnboardingChecklist status={obStatus} onDismiss={dismiss} />
            )}

            {/* Metric cards */}
            <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                <Metric icon={Users} label="Total fans" value={s.total_fans.toLocaleString()} testId="metric-total-fans" />
                <Metric icon={CalendarDays} label="Upcoming events" value={s.upcoming_events} testId="metric-upcoming" />
                <Metric icon={Ticket} label="Registrations" value={s.total_registrations.toLocaleString()} testId="metric-regs" />
                <Metric icon={Users} label="Attendees" value={s.total_attendees.toLocaleString()} testId="metric-attendees" />
                <Metric icon={TrendingUp} label="Returning fans" value={s.returning_fans.toLocaleString()} testId="metric-returning" />
                <Metric icon={TrendingUp} label="Avg attendance" value={`${s.avg_attendance_rate}%`} testId="metric-avg-rate" />
            </section>

            {canSeeFollowers && fm && (
                <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]" data-testid="follower-metrics-section">
                    <Metric icon={Heart} label="Current followers" value={fm.current_followers.toLocaleString()} testId="ov-follower-current" />
                    <Metric icon={TrendingUp} label="Gained · 30 days" value={fm.new_last_30_days.toLocaleString()} testId="ov-follower-30d" />
                    {fm.top_source ? (
                        <Link to="/workspace/audience" className="bg-[#0a0a0a] p-5 hover:bg-[#141414]" data-testid="ov-top-source">
                            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#666]">
                                <Sparkles className="w-3 h-3" /> Top source
                            </div>
                            <div className="mt-2 font-display font-black uppercase tracking-tighter text-2xl md:text-3xl text-[#ff4400]" data-testid="ov-top-source-label">
                                {fm.top_source.label}
                            </div>
                            <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1 truncate" data-testid="ov-top-source-sub">
                                +{fm.top_source.count_30} · {fm.top_source.percentage_window}% · {fm.top_source_window_label}
                            </div>
                        </Link>
                    ) : (
                        <Metric icon={Sparkles} label="Top source" value="—" sub="No tracked follows in 30 days" testId="ov-top-source-empty" />
                    )}
                    {fm.best_event_by_growth ? (
                        <Link to={`/workspace/events/${fm.best_event_by_growth.event_id}/performance`} className="bg-[#0a0a0a] p-5 hover:bg-[#141414]" data-testid="ov-best-event-growth">
                            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#666]">
                                <Sparkles className="w-3 h-3" /> Strongest event
                            </div>
                            <div className="mt-2 font-display font-black uppercase tracking-tighter text-2xl md:text-3xl text-[#ff4400]">
                                +{fm.best_event_by_growth.window_total}
                            </div>
                            <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1 truncate">{fm.best_event_by_growth.event_name}</div>
                        </Link>
                    ) : (
                        <Metric icon={Sparkles} label="Strongest event" value="—" sub="No completed events" testId="ov-best-event-growth-empty" />
                    )}
                </section>
            )}

            {/* Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                {/* Upcoming events */}
                <Panel title="Upcoming events" kicker="Live" className="lg:col-span-2">
                    {data.upcoming_events.length === 0 ? (
                        <div className="text-sm text-[#a3a3a3]" data-testid="dashboard-no-upcoming">No upcoming events yet.</div>
                    ) : (
                        <div className="border border-[#2a2a2a]">
                            {data.upcoming_events.map((e) => (
                                <div key={e.id} className="flex items-center gap-4 p-4 border-b border-[#2a2a2a] last:border-b-0" data-testid={`dashboard-event-${e.id}`}>
                                    <div className="text-center border border-[#2a2a2a] px-3 py-2 min-w-[80px]">
                                        <div className="font-display font-black text-[#ff4400]">{formatDate(e.date).split(",")[0]}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#666]">{e.start_time}</div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold uppercase text-sm">{e.name}</div>
                                        <div className="text-xs text-[#a3a3a3] mt-1 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> {e.venue_name} · {e.city}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-display font-bold">{e.registrations}/{e.capacity}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#666]">registered</div>
                                    </div>
                                    <Link to={`/workspace/check-in/${e.id}`} className="pm-btn text-xs" data-testid={`dashboard-checkin-link-${e.id}`}>
                                        Check-in
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>

                {/* Tasks */}
                <Panel title="Needs attention" kicker="Tasks">
                    {data.tasks.length === 0 ? (
                        <div className="text-sm text-[#a3a3a3]">Nothing urgent.</div>
                    ) : (
                        <div className="space-y-2">
                            {data.tasks.map((t, i) => (
                                <div key={i} className="border border-[#2a2a2a] p-3 flex items-start gap-2" data-testid={`dashboard-task-${i}`}>
                                    <AlertTriangle className="w-4 h-4 text-[#ffea00] shrink-0 mt-0.5" />
                                    <div className="text-xs text-[#f4f4f0]">{t.message}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                {/* Fan growth */}
                <Panel title="Follower growth" kicker="Last 5 weeks">
                    <div className="space-y-3">
                        {data.fan_growth.map((f, idx) => {
                            const max = Math.max(...data.fan_growth.map((x) => x.fans));
                            const width = max ? Math.round((f.fans / max) * 100) : 0;
                            const key = f.label || f.month || `w-${idx}`;
                            return (
                                <div key={key} data-testid={`fan-growth-${key}`}>
                                    <div className="flex items-center justify-between text-xs font-mono uppercase text-[#a3a3a3]">
                                        <span>{f.label || f.month} {f.new > 0 && <span className="text-[#00e676]">+{f.new}</span>}</span>
                                        <span className="text-[#f4f4f0]">{f.fans.toLocaleString()}</span>
                                    </div>
                                    <div className="h-2 bg-[#141414] mt-1"><div className="h-full bg-[#ff4400]" style={{ width: `${width}%` }} /></div>
                                </div>
                            );
                        })}
                    </div>
                </Panel>

                {/* Strongest cities */}
                <Panel title="Strongest cities" kicker="Registrations" icon={MapPin}>
                    <RankedList items={data.strongest_cities.map((c) => ({ key: c.city, label: c.city, value: c.registrations }))} />
                </Panel>

                {/* Strongest countries */}
                <Panel title="Strongest countries" kicker="Registrations" icon={Globe2}>
                    <RankedList items={data.strongest_countries.map((c) => ({ key: c.country, label: c.country, value: c.registrations }))} />
                </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#2a2a2a]">
                {/* Recent activity */}
                <Panel title="Recent activity" kicker="Latest 10">
                    {data.recent_activity.length === 0 ? (
                        <div className="text-sm text-[#a3a3a3]">No activity yet.</div>
                    ) : (
                        <div className="border border-[#2a2a2a]">
                            {data.recent_activity.map((r) => (
                                <div key={r.id} className="p-3 border-b border-[#2a2a2a] last:border-b-0 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-[#141414] border border-[#2a2a2a] flex items-center justify-center text-[10px] font-mono uppercase">{r.fan_name.charAt(0)}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-display font-bold uppercase truncate">{r.fan_name}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#666] truncate">{r.event_name}</div>
                                    </div>
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">
                                        {r.checked_in ? "checked in" : "registered"}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>

                {/* Event performance */}
                <Panel title="Event performance" kicker="Fill rate">
                    <div className="space-y-2">
                        {data.event_performance.map((p) => (
                            <div key={p.id} data-testid={`event-perf-${p.id}`}>
                                <div className="flex items-center justify-between text-xs font-mono">
                                    <span className="uppercase truncate mr-2">{p.name}</span>
                                    <span className="text-[#a3a3a3]">{p.registrations}/{p.capacity} · {p.fill_rate}%</span>
                                </div>
                                <div className="h-1.5 bg-[#141414] mt-1"><div className="h-full bg-[#ff4400]" style={{ width: `${Math.min(100, p.fill_rate)}%` }} /></div>
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>
        </div>
    );
}

function Metric({ icon: Icon, label, value, testId, sub }) {
    return (
        <div className="bg-[#0a0a0a] p-5" data-testid={testId}>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#666]">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="mt-2 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">{value}</div>
            {sub && <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1 truncate">{sub}</div>}
        </div>
    );
}

function Panel({ title, kicker, icon: Icon, className = "", children }) {
    return (
        <div className={`bg-[#0a0a0a] p-6 ${className}`}>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#666]">
                {Icon && <Icon className="w-3 h-3" />} {kicker}
            </div>
            <div className="font-display font-black uppercase tracking-tighter text-xl md:text-2xl mt-1 mb-4">{title}</div>
            {children}
        </div>
    );
}

function RankedList({ items }) {
    if (!items.length) return <div className="text-sm text-[#a3a3a3]">No data yet.</div>;
    const max = Math.max(...items.map((i) => i.value)) || 1;
    return (
        <div className="space-y-2">
            {items.map((it, i) => (
                <div key={it.key} data-testid={`ranked-${it.key}`}>
                    <div className="flex items-center justify-between text-xs font-mono uppercase">
                        <span className="text-[#a3a3a3]">{String(i + 1).padStart(2, "0")} · {it.label}</span>
                        <span className="text-[#f4f4f0]">{it.value.toLocaleString()}</span>
                    </div>
                    <div className="h-1 bg-[#141414] mt-1"><div className="h-full bg-[#ff4400]" style={{ width: `${(it.value / max) * 100}%` }} /></div>
                </div>
            ))}
        </div>
    );
}

const CHECKLIST_LABELS = {
    profile_image: "Add a profile image",
    cover_image: "Add a cover image",
    bio: "Write a short bio",
    primary_genre: "Set your primary genre",
    location: "Add city and country",
    music_links: "Add music links",
    releases: "Add a release",
    social_links: "Add social links",
    merch: "Add merchandise",
    team: "Invite a teammate",
    first_event: "Create your first event",
};

const CHECKLIST_LINK = {
    profile_image: "/workspace/artist-page",
    cover_image: "/workspace/artist-page",
    bio: "/workspace/artist-page",
    primary_genre: "/workspace/artist-page",
    location: "/workspace/artist-page",
    music_links: "/workspace/artist-page",
    releases: "/workspace/artist-page",
    social_links: "/workspace/artist-page",
    merch: "/workspace/artist-page",
    team: "/workspace/team",
    first_event: "/workspace/events/new",
};

function OnboardingChecklist({ status, onDismiss }) {
    const items = Object.entries(status.checklist || {});
    return (
        <section
            className="border-b border-[#2a2a2a] bg-[#141414] px-4 md:px-8 py-5"
            data-testid="onboarding-checklist"
        >
            <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">
                        {status.visibility === "public" ? "Keep growing" : "Complete your artist profile"}
                    </div>
                    <div className="mt-1 font-display font-bold uppercase text-lg md:text-xl">
                        {status.done}/{status.total} done · {status.percent}%
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="text-[#a3a3a3] hover:text-[#ff4400]"
                    aria-label="Hide checklist"
                    data-testid="onboarding-checklist-dismiss"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="h-1 bg-[#0a0a0a] mb-4">
                <div className="h-full bg-[#ff4400] transition-all" style={{ width: `${status.percent}%` }} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {items.map(([key, done]) => (
                    <Link
                        key={key}
                        to={CHECKLIST_LINK[key] || "/workspace/artist-page"}
                        className={`flex items-center gap-2 border px-3 py-2 text-xs ${
                            done ? "border-[#2a2a2a] text-[#666] line-through" : "border-[#2a2a2a] hover:border-[#ff4400] text-[#f4f4f0]"
                        }`}
                        data-testid={`checklist-item-${key}`}
                    >
                        {done ? (
                            <CheckCircle2 className="w-4 h-4 text-[#00e676] shrink-0" />
                        ) : (
                            <Circle className="w-4 h-4 text-[#ff4400] shrink-0" />
                        )}
                        <span className="uppercase font-mono tracking-wide">{CHECKLIST_LABELS[key] || key}</span>
                    </Link>
                ))}
            </div>
        </section>
    );
}
